import { useEffect, useRef, useState } from "react";
import { useAuth } from "./context/AuthContext";

function SignaturePad({ height = 160, onChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const heightCss = height;
    canvas.width = width * dpr;
    canvas.height = heightCss * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
  }, [height]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e && e.touches.length > 0) {
      const t = e.touches[0];
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    } else if ("clientX" in e) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    return { x: 0, y: 0 };
  };

  const draw = (x, y, move = false) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!move) ctx.beginPath();
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
    if (onChange) onChange(canvas.toDataURL("image/png"));
  };

  const handleStart = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getPos(e);
    draw(x, y, false);
  };

  const handleMove = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    draw(x, y, true);
  };

  const handleEnd = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    if (onChange) onChange("");
  };

  return (
    <div className="w-100">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="small text-muted mb-0">Поставьте подпись ниже (мышью или пальцем)</p>
        <button type="button" onClick={clear} className="btn btn-link p-0 small">Очистить</button>
      </div>
      <div className="border rounded bg-white shadow-sm overflow-hidden" onMouseLeave={handleEnd}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          style={{ width: "100%", height: `${height}px`, touchAction: "none", cursor: "crosshair" }}
        />
      </div>
      {!hasSignature && (
        <p className="mt-2 text-muted small">Подпись должна соответствовать документу. Юридическая значимость зависит от политики вашей организации.</p>
      )}
    </div>
  );
}


const scrollToPainPoints = () => {
  const painPointsSection = document.getElementById('pain-points-section');
  if (painPointsSection) {
    painPointsSection.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  }
};

function SectionHeading({ kicker, title, subtitle }) {
  return (
    <div className="text-center mb-5">
      {kicker && <div className="text-uppercase fw-medium text-primary small">{kicker}</div>}
      <h2 className="fw-bold">{title}</h2>
      {subtitle && <p className="text-muted mx-auto" style={{ maxWidth: "600px" }}>{subtitle}</p>}
    </div>
  );
}

function Logo({ small = false }) {
  useEffect(() => {
    const updateLogoVars = () => {
      const w = window.innerWidth || 360;
      const titleSize = Math.max(12, Math.min(34.375, w * 0.05));
      const subtitleSize = Math.max(10, Math.min(25, w * 0.032));
      const iconSize = Math.max(24, Math.min(62.5, w * 0.075));
      const iconSvgSize = Math.max(14, Math.min(31.25, w * 0.0375));
      const root = document.documentElement;
      root.style.setProperty('--logo-title-size', `${titleSize}px`);
      root.style.setProperty('--logo-subtitle-size', `${subtitleSize}px`);
      root.style.setProperty('--logo-icon-size', `${iconSize}px`);
      root.style.setProperty('--logo-icon-svg-size', `${iconSvgSize}px`);
    };
    updateLogoVars();
    window.addEventListener('resize', updateLogoVars);
    return () => window.removeEventListener('resize', updateLogoVars);
  }, []);

  return (
    <div className={`d-inline-flex align-items-center gap-2 app-logo ${small ? "small" : ""}`}>
      <div className="d-flex align-items-center justify-content-center rounded app-logo__icon" style={{ backgroundColor: "#2e59d9" }}>
        <svg viewBox="0 0 24 24" className="app-logo__icon-svg" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12l4 4 12-12" />
        </svg>
      </div>
      <div className="app-logo__text">
        <div className="fw-bold app-logo__title" style={{ color: "#2e59d9" }}>Просто Подпиши</div>
        <div className="text-muted app-logo__subtitle">Автоматизация документов</div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { authFetch } = useAuth();
  const [sig, setSig] = useState("");
  const [activeFaq, setActiveFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    consent: false
  });

  // Removed JS auto-fit; CSS clamp() handles scaling now

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    alert("Демо-форма отправлена. Подпись захвачена: " + (sig ? "да" : "нет"));
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      alert('Необходимо дать согласие на обработку персональных данных');
      return;
    }

    try {
      const response = await authFetch('/api/v1/landing-form-submission', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          contact: formData.contact,
          consent: formData.consent
        })
      });

      if (response.ok) {
        alert('Форма отправлена! Мы свяжемся с вами в ближайшее время.');
        setIsModalOpen(false);
        setFormData({ name: '', contact: '', consent: false });
      } else if (response.status === 429) {
        alert('Слишком много запросов с вашего IP-адреса. Пожалуйста, подождите несколько минут и попробуйте снова.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Ошибка при отправке формы';
        alert(`Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Произошла ошибка при отправке формы. Попробуйте еще раз или свяжитесь с нами напрямую.' + error);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    
    <div style={{ background: "linear-gradient(180deg, #f6f8ff 0%, #ffffff 40%, #f7fbff 100%)", position: "relative" }}>
      <style>{` 
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Unbounded:wght@200..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');

        /* Responsive Logo - scales with viewport to prevent overflow on phones */
        .app-logo { gap: 0.5rem; max-width: 100%; }
        .app-logo__text { min-width: 0; }
        .app-logo__icon { width: var(--logo-icon-size, 36px); height: var(--logo-icon-size, 36px); border-radius: 0.5rem; }
        .app-logo__icon-svg { width: var(--logo-icon-svg-size, 18px); height: var(--logo-icon-svg-size, 18px); }
        .app-logo__title { white-space: nowrap; line-height: 1.15; font-size: var(--logo-title-size, 18px); }
        .app-logo__subtitle { white-space: nowrap; line-height: 1.15; font-size: var(--logo-subtitle-size, 14px); }
        .app-logo.small .app-logo__icon { width: calc(var(--logo-icon-size, 36px) * 0.8); height: calc(var(--logo-icon-size, 36px) * 0.8); }
        .app-logo.small .app-logo__icon-svg { width: calc(var(--logo-icon-svg-size, 18px) * 0.9); height: calc(var(--logo-icon-svg-size, 18px) * 0.9); }
        .app-logo.small .app-logo__title { font-size: calc(var(--logo-title-size, 18px) * 0.9); }
        .app-logo.small .app-logo__subtitle { font-size: calc(var(--logo-subtitle-size, 14px) * 0.85); }
        @media (max-width: 576px) {
          .app-logo { gap: 0.4rem; }

          #style-text {
            padding:0;
          }
        }

        * {
          font-family: "Montserrat", sans-serif;
        }
        
        .qwe {
          margin-top:25rem;
        }
        #hero-container {
          height:100vh;
          background: url("/ComfyUI_00258_(1).png");
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          padding: 0;
          margin: 0;
          position: relative;
          width: 100%;
          z-index: 1;
        }
        #style-text {
          font-family: "Press Start 2P", sans-serif;
          color:#003dda;
          /* min 16px, fluid 6vw, max 64px */
          font-size: clamp(16px, 6vw, 64px);
          line-height: 1.05;
          letter-spacing: -0.02em;
          font-weight:800;
          position:absolute;
          top: 50%;
          left: 5vw;
          transform: translateY(-50%);
          max-width: 90vw;
          white-space: normal;
          text-align: left;
        }
        #style-text span { font-family: "Press Start 2P", sans-serif !important; font-weight: 800; }
        #style-text span.subtitle { 
          font-family: "Montserrat", sans-serif !important;
          font-weight: normal !important; 
          font-size: 0.75em; 
        }
        #hero-button {
          position: absolute;
          bottom: 15vh;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          z-index: 2;
        }
        #hero-button .btn {
          background-color: #003dda;
          border: none;
          border-radius: 0.75rem;
          padding: 2rem 5rem;
          font-size: 1.6rem;
          font-weight: 600;
          min-width: 400px;
          box-shadow: 0 4px 15px rgba(0, 61, 218, 0.3);
          transition: all 0.3s ease;
        }
        #hero-button .btn:hover {
          background-color: #1e40af;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 61, 218, 0.4);
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }
        .modal-content {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          position: relative;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #64748b;
          cursor: pointer;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
        }
        .modal-close:hover {
          background-color: #f1f5f9;
          color: #1e293b;
        }
        .form-group {
          margin-bottom: 1.5rem;
        }
        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
        }
        .form-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }
        .form-input:focus {
          outline: none;
          border-color: #003dda;
          box-shadow: 0 0 0 3px rgba(0, 61, 218, 0.1);
        }
        .checkbox-group {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 2rem;
        }
        .checkbox-input {
          margin-top: 0.25rem;
          flex-shrink: 0;
        }
        .checkbox-label {
          font-size: 0.9rem;
          color: #6b7280;
          line-height: 1.4;
        }
        .form-submit {
          width: 100%;
          background-color: #003dda;
          color: white;
          border: none;
          border-radius: 0.5rem;
          padding: 1rem 2rem;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .form-submit:hover {
          background-color: #1e40af;
          transform: translateY(-1px);
        }
        .new-section {
          background: white;
          padding:2em;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
          z-index: 10;
        }
        .new-section-content {
          text-align: center;
          max-width: 800px;
        }
        .legal-section {
          background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
          padding: 4rem 2rem;
          position: relative;
          z-index: 10;
        }
        .legal-content {
          max-width: 1000px;
          margin: 0 auto;
        }
        .legal-badge {
          display: inline-block;
          background: #003dda;
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 2rem;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 2rem;
        }
        .stats-section {
          background: linear-gradient(135deg, #003dda 0%, #1e40af 50%, #3b82f6 100%);
          color: white;
          padding: 5rem 2rem;
          position: relative;
          z-index: 10;
          overflow: hidden;
        }
        .stats-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          opacity: 0.3;
        }
        .stats-content {
          position: relative;
          z-index: 2;
        }
        .stat-item {
          text-align: center;
          padding: 2rem 1rem;
        }
        .stat-number {
          font-size: 4rem;
          font-weight: 900;
          line-height: 1;
          margin-bottom: 0.5rem;
          background: linear-gradient(45deg, #ffffff, #e0e7ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
        }
        .stat-label {
          font-size: 1.2rem;
          font-weight: 600;
          opacity: 0.9;
          margin-bottom: 0.5rem;
        }
        .stat-description {
          font-size: 0.9rem;
          opacity: 0.8;
        }
        .pulse-animation {
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        .demo-section {
          background: white;
          padding: 5rem 2rem;
          position: relative;
          z-index: 10;
        }
        .demo-content {
          max-width: 1200px;
          margin: 0 auto;
        }
        .demo-container {
          background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
          border-radius: 2rem;
          padding: 3rem;
          box-shadow: 0 20px 40px rgba(0, 61, 218, 0.1);
          border: 1px solid rgba(0, 61, 218, 0.1);
        }
        .demo-header {
          text-align: center;
          margin-bottom: 3rem;
        }
        .demo-badge {
          display: inline-block;
          background: #003dda;
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 2rem;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }
        .demo-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
        }
        .demo-text {
          padding: 1rem;
        }
        .demo-signature {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }
        .demo-steps {
          list-style: none;
          padding: 0;
        }
        .demo-steps li {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          padding: 1rem;
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }
        .demo-steps .step-number {
          background: #003dda;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin-right: 1rem;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .demo-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
        }
        .pricing-section {
          background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
          padding: 5rem 2rem;
          position: relative;
          z-index: 10;
        }
        .pricing-content {
          max-width: 1200px;
          margin: 0 auto;
        }
        .pricing-header {
          text-align: center;
          margin-bottom: 4rem;
        }
        .pricing-badge {
          display: inline-block;
          background: #003dda;
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 2rem;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-bottom: 3rem;
        }
        .pricing-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          border: 2px solid transparent;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .pricing-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }
        .pricing-card.popular {
          border-color: #003dda;
          transform: scale(1.05);
        }
        .pricing-card.popular::before {
          content: 'ПОПУЛЯРНЫЙ';
          position: absolute;
          top: 0;
          right: 0;
          background: #003dda;
          color: white;
          padding: 0.5rem 1.5rem;
          font-size: 0.8rem;
          font-weight: 600;
          border-bottom-left-radius: 1rem;
        }
        .pricing-card.popular:hover {
          transform: scale(1.05) translateY(-5px);
        }
        .plan-name {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }
        .plan-description {
          color: #64748b;
          margin-bottom: 2rem;
          font-size: 0.95rem;
        }
        .plan-price {
          margin-bottom: 2rem;
        }
        .price-amount {
          font-size: 3rem;
          font-weight: 900;
          color: #003dda;
          line-height: 1;
        }
        .price-period {
          color: #64748b;
          font-size: 1rem;
          margin-left: 0.5rem;
        }
        .plan-features {
          list-style: none;
          padding: 0;
          margin-bottom: 2rem;
        }
        .plan-features li {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }
        .plan-features .feature-icon {
          color: #10b981;
          margin-right: 0.75rem;
          font-size: 1.1rem;
        }
        .plan-button {
          width: 100%;
          padding: 1rem 2rem;
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .plan-button.primary {
          background: #003dda;
          color: white;
        }
        .plan-button.primary:hover {
          background: #1e40af;
          transform: translateY(-2px);
        }
        .plan-button.secondary {
          background: #f1f5f9;
          color: #003dda;
          border: 2px solid #003dda;
        }
        .plan-button.secondary:hover {
          background: #003dda;
          color: white;
        }
        .pricing-note {
          text-align: center;
          background: white;
          padding: 2rem;
          border-radius: 1rem;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
        .faq-section {
          background: white;
          padding: 5rem 2rem;
          position: relative;
          z-index: 10;
        }
        .faq-content {
          max-width: 1000px;
          margin: 0 auto;
        }
        .faq-header {
          text-align: center;
          margin-bottom: 4rem;
        }
        .faq-badge {
          display: inline-block;
          background: #003dda;
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 2rem;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }
        .faq-accordion {
          max-width: 800px;
          margin: 0 auto;
        }
        .faq-item {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 1rem;
          margin-bottom: 1rem;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }
        .faq-item:hover {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        .faq-question {
          background: #f8f9ff;
          padding: 1.5rem 2rem;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
          font-size: 1.1rem;
          color: #1e293b;
          transition: all 0.3s ease;
        }
        .faq-question:hover {
          background: #e8f2ff;
        }
        .faq-question.active {
          background: #003dda;
          color: white;
        }
        .faq-icon {
          font-size: 1.2rem;
          transition: transform 0.3s ease;
        }
        .faq-question.active .faq-icon {
          transform: rotate(45deg);
        }
        .faq-answer {
          padding: 0 2rem;
          max-height: 0;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        .faq-answer.active {
          padding: 1.5rem 2rem;
          max-height: 500px;
        }
        .faq-answer p {
          margin-bottom: 1rem;
          color: #64748b;
          line-height: 1.6;
        }
        .faq-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-answer ul {
          margin: 1rem 0;
          padding-left: 1.5rem;
        }
        .faq-answer li {
          margin-bottom: 0.5rem;
          color: #64748b;
        }
        .faq-contact {
          text-align: center;
          margin-top: 3rem;
          padding: 2rem;
          background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
          border-radius: 1rem;
        }
        .scroll-indicator {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 3;
          opacity: 1;
          transition: opacity 0.3s ease;
        }
        .scroll-arrow {
          width: 5vw;
          height: 5vw;
          border-right: 1vw solid #003dda;
          border-bottom: 1vw solid #003dda;
          transform: rotate(45deg);
          animation: bounce 2s infinite;
        }
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: rotate(45deg) translateY(0);
          }
          40% {
            transform: rotate(45deg) translateY(-1vw) translateX(-1vw);
          }
          60% {
            transform: rotate(45deg) translateY(-2vw) translateX(-2vw);
          }
        }
      `}</style>
      <div className="dots" />
      <div className="content-layer">

      <section className="py-0">
        <div className="container-fluid" id="hero-container">
          <div className="row align-items-center g-5 h-100">
            <div id="style-text"><span>ПРОСТО</span> <span>ПОДПИШИ</span>
            <br />
            <span className="subtitle">Автоматизируйте учебный документооборот за 1 день</span>
            </div>
            <div id="hero-button">
              <button className="btn btn-primary btn-lg" onClick={openModal}>Запросить демоверсию</button>
            </div> 
          </div>
        </div> 
      </section>

      {/* Pain Points Section */}
      <div className="new-section" id="pain-points-section">
        <div className="new-section-content p-5">
          <h2 className="mb-4 text-center">Проблема, которую мы решаем</h2>
          <p className="text-center text-muted mb-5" style={{maxWidth: '600px', margin: '0 auto'}}>
            Подготовка дополнительных соглашений и приказов — это десятки часов ручной работы и риск ошибок в формулировках. 
            Каждая неточность может вызвать вопросы при проверке, а владельцу приходится лично контролировать каждый документ.
          </p>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 text-center border-0 shadow-sm">
                <div className="card-body">
                  <i className="fas fa-clock mb-3" style={{fontSize: '2.5rem', color: '#003dda'}}></i>
                  <h5 className="card-title fw-bold text-dark">Десятки часов</h5>
                  <p className="card-text text-muted">
                    Ручная работа на подготовку дополнительных соглашений и приказов отнимает много времени
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="card h-100 text-center border-0 shadow-sm">
                <div className="card-body">
                  <i className="fas fa-exclamation-triangle mb-3" style={{fontSize: '2.5rem', color: '#003dda'}}></i>
                  <h5 className="card-title fw-bold text-dark">Риск ошибок</h5>
                  <p className="card-text text-muted">
                    Каждая неточность в формулировках может вызвать вопросы при проверке
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="card h-100 text-center border-0 shadow-sm">
                <div className="card-body">
                  <i className="fas fa-user-check mb-3" style={{fontSize: '2.5rem', color: '#003dda'}}></i>
                  <h5 className="card-title fw-bold text-dark">Личный контроль</h5>
                  <p className="card-text text-muted">
                    Владельцу приходится лично контролировать каждый документ
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-5">
            <div className="d-inline-block p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #e9ecef'}}>
              <p className="mb-2 fw-bold text-dark" style={{fontSize: '1.2rem'}}>Наш сервис решает эту проблему</p>
              <p className="mb-0 text-muted">
                Он формирует юридически корректные документы по утверждённым шаблонам, минимизирует ошибки и освобождает команду от рутины
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Steps Section */}
      <div className="legal-section">
        <div className="legal-content">
          <div className="text-center mb-5">
            <div className="legal-badge">КАК ЭТО РАБОТАЕТ</div>
            <h2 className="mb-4">Этапы работы с сервисом</h2>
            <p className="lead text-muted">
              Простой и понятный процесс автоматизации документооборота
            </p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{width: '60px', height: '60px'}}>
                  <span className="fw-bold fs-4">1</span>
                </div>
                <h5 className="mb-2">Настройка</h5>
                <p className="text-muted mb-0">
                  Один раз согласуем шаблоны документов под вашу организацию
                </p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{width: '60px', height: '60px'}}>
                  <span className="fw-bold fs-4">2</span>
                </div>
                <h5 className="mb-2">Формирование</h5>
                <p className="text-muted mb-0">
                  Сервис автоматически создаёт документы по шаблонам
                </p>
                <ul className="list-unstyled mt-2 small">
                  <li>• дополнительное соглашение</li>
                  <li>• приказ о зачислении</li>
                  <li>• ведомость аттестации</li>
                  <li>• приказ об отчислении</li>
                </ul>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{width: '60px', height: '60px'}}>
                  <span className="fw-bold fs-4">3</span>
                </div>
                <h5 className="mb-2">Подпись ученика</h5>
                <p className="text-muted mb-0">
                  Документы подписываются онлайн: пальцем или мышью
                </p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{width: '60px', height: '60px'}}>
                  <span className="fw-bold fs-4">4</span>
                </div>
                <h5 className="mb-2">Хранение</h5>
                <p className="text-muted mb-0">
                  Документы сохраняются в системе и всегда доступны для проверки
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="stats-section">
        <div className="stats-content">
          <div className="text-center mb-5">
            <h1 className="mb-4" style={{fontSize: '2.5rem'}}>Преимущества для вашего бизнеса</h1>
            <p className="lead opacity-90" style={{fontSize: '1.3rem'}}>
              Автоматизация документооборота приносит реальную пользу
            </p>
          </div>
          
          <div className="row g-2">
            <div className="col-lg-6 col-md-6">
              <div className="stat-item pulse-animation" style={{padding: '1rem'}}>
                <div className="stat-label" style={{fontSize: '2.5rem', marginBottom: '0.5rem', fontWeight: 'bold'}}>Экономия времени</div>
                <div className="stat-description" style={{fontSize: '1.1rem', maxWidth: '80%', margin: '0 auto'}}>Автоматизация сокращает рутину и снижает количество ошибок до нуля</div>
              </div>
            </div>
            
            <div className="col-lg-6 col-md-6">
              <div className="stat-item pulse-animation" style={{animationDelay: '0.5s', padding: '1rem'}}>
                <div className="stat-label" style={{fontSize: '2.5rem', marginBottom: '0.5rem', fontWeight: 'bold'}}>Юридическая корректность</div>
                <div className="stat-description" style={{fontSize: '1.1rem', maxWidth: '80%', margin: '0 auto'}}>Документы полностью соответствуют законодательству РФ и автоматически обновляются при изменениях</div>
              </div>
            </div>
            
            <div className="col-lg-6 col-md-6">
              <div className="stat-item pulse-animation" style={{animationDelay: '1s', padding: '1rem'}}>
                <div className="stat-label" style={{fontSize: '2.5rem', marginBottom: '0.5rem', fontWeight: 'bold'}}>Системность</div>
                <div className="stat-description" style={{fontSize: '1.1rem', maxWidth: '80%', margin: '0 auto'}}>Все документы хранятся в одном месте, с удобным поиском и доступом для сотрудников</div>
              </div>
            </div>
            
            <div className="col-lg-6 col-md-6">
              <div className="stat-item pulse-animation" style={{animationDelay: '1.5s', padding: '1rem'}}>
                <div className="stat-label" style={{fontSize: '2.5rem', marginBottom: '0.5rem', fontWeight: 'bold'}}>Готовность к проверкам</div>
                <div className="stat-description" style={{fontSize: '1.1rem', maxWidth: '80%', margin: '0 auto'}}>Весь пакет документов всегда в порядке и готов к проверкам Рособрнадзора</div>
              </div>
            </div>
          </div>
          
          <div className="row mt-3">
            <div className="col-md-12">
              <div className="text-center">
                <div className="stat-item pulse-animation" style={{padding: '1rem'}}>
                  <div className="stat-label" style={{fontSize: '2.5rem', marginBottom: '0.5rem', fontWeight: 'bold'}}>Оптимизация затрат</div>
                  <div className="stat-description" style={{fontSize: '1.1rem', maxWidth: '60%', margin: '0 auto'}}>Не нужно тратить деньги на юриста или ручную сверку шаблонов</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-5">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="bg-white bg-opacity-10 rounded-3 p-4 backdrop-blur">
                  <h3 className="mb-3" style={{fontSize: '1.8rem'}}>Результат</h3>
                  <p className="mb-0 opacity-90" style={{fontSize: '1.2rem'}}>
                    Сервис помогает сократить время на оформление документов с нескольких часов до пары минут — без ошибок и повторных проверок.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Compliance Section */}
      <div className="legal-section">
        <div className="legal-content">
          <div className="text-center mb-5">
            <div className="legal-badge">ЮРИДИЧЕСКАЯ ЗНАЧИМОСТЬ</div>
            <h2 className="mb-4">Подписание документов — безопасно и юридически корректно</h2>
            <p className="lead text-muted">
              Договоры и дополнительные соглашения, подписанные через наш сервис, обладают такой же юридической силой, как и бумажные документы.
            </p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-12">
              <div className="bg-white rounded-3 p-4 shadow-sm">
                <ul className="list-unstyled">
                  <li className="mb-3" style={{fontSize: '1.2rem'}}>
                    <i className="fas fa-check-circle text-primary me-2" style={{fontSize: '1.3rem'}}></i>
                    <strong>Пункт 2 статьи 434 Гражданского кодекса РФ</strong> допускает заключение договоров в письменной форме, в том числе в электронном виде.
                  </li>
                  <li className="mb-3" style={{fontSize: '1.2rem'}}>
                    <i className="fas fa-check-circle text-primary me-2" style={{fontSize: '1.3rem'}}></i>
                    <strong>Подписание осуществляется с использованием простой электронной подписи (ПЭП)</strong>, которая является законным аналогом собственноручной подписи.
                  </li>
                  <li className="mb-3" style={{fontSize: '1.2rem'}}>
                    <i className="fas fa-check-circle text-primary me-2" style={{fontSize: '1.3rem'}}></i>
                    <strong>В каждый документ автоматически включается соглашение</strong> об использовании электронной подписи и её реквизиты.
                  </li>
                </ul>
                
                <div className="mt-4 p-3 bg-light rounded">
                  <h4 className="text-primary mb-2" style={{fontSize: '1.6rem'}}>Результат:</h4>
                  <p className="mb-0 fw-bold" style={{fontSize: '1.3rem'}}>
                    Документы юридически значимы и готовы к любой проверке.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Section */}
      <div className="demo-section">
        <div className="demo-content">
          <div className="demo-container">
            <div className="demo-header">
              <div className="demo-badge">ДЕМОНСТРАЦИЯ РАБОТЫ</div>
              <h2 className="mb-4">Демонстрация работы сервиса</h2>
              <p className="lead text-muted">
                Посмотрите, как работает автоматизация со стороны ученика
              </p>
            </div>
            
            <div className="demo-grid">
              <div className="demo-text">
                <h4 className="mb-4">Простой процесс подписания:</h4>
                <ul className="demo-steps">
                  <li>
                    <div className="step-number">1</div>
                    <div>
                      <strong>Выбираете документ</strong><br/>
                      <small className="text-muted">Выбираете нужный тип документа из списка</small>
                    </div>
                  </li>
                  <li>
                    <div className="step-number">2</div>
                    <div>
                      <strong>Заполняете ключевые данные</strong><br/>
                      <small className="text-muted">Вводите необходимую информацию в поля формы</small>
                    </div>
                  </li>
                  <li>
                    <div className="step-number">3</div>
                    <div>
                      <strong>Подписываете онлайн</strong><br/>
                      <small className="text-muted">Ставите подпись пальцем или мышью</small>
                    </div>
                  </li>
                  <li>
                    <div className="step-number">4</div>
                    <div>
                      <strong>Получаете готовый документ</strong><br/>
                      <small className="text-muted">Документ сохраняется в системе и доступен для скачивания</small>
                    </div>
                  </li>
                </ul>
                
                <div className="mt-4 text-center">
                  <a href="https://t.me/siliennnce" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                    Протестировать сервис бесплатно
                  </a>
                  <p className="small text-muted mt-2">
                    Мы подключим демо-аккаунт и вы попробуете подписать документ на реальном примере
                  </p>
                </div>
              </div>
              
              <div className="demo-signature">
                <h5 className="text-center mb-3">Попробуйте подпись прямо сейчас:</h5>
                <SignaturePad height={200} onChange={(signature) => {
                  if (signature) {
                    console.log('Signature captured!');
                  }
                }} />
                <div className="text-center mt-3">
                  <small className="text-muted">
                    Нарисуйте вашу подпись в поле выше
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Target Audience Section */}
      <div className="pricing-section">
        <div className="pricing-content">
          <div className="pricing-header">
            <div className="pricing-badge">ДЛЯ КОГО ПОДХОДИТ</div>
            <h2 className="mb-4">Кому подходит сервис</h2>
            <p className="lead text-muted">
              Наши решения идеально подходят для образовательных организаций
            </p>
          </div>
          
          <div className="pricing-grid">
            {/* Online Schools */}
            <div className="pricing-card">
              <div className="plan-name">✅ Онлайн-школам и образовательным центрам</div>
              <div className="plan-description">Автоматизация документооборота для дистанционного обучения</div>
              <ul className="plan-features">
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Автоматическое формирование договоров
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Электронное подписание документов
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Централизованное хранение
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Готовность к проверкам Рособрнадзора
                </li>
              </ul>
            </div>

            {/* Individual Entrepreneurs */}
            <div className="pricing-card popular">
              <div className="plan-name">✅ ИП, оказывающим образовательные услуги</div>
              <div className="plan-description">Простое решение для индивидуальных предпринимателей</div>
              <ul className="plan-features">
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Шаблоны под разные виды деятельности
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Работа с лицензией и без неё
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Юридическая корректность документов
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Экономия времени и ресурсов
                </li>
              </ul>
            </div>

            {/* All Organizations */}
            <div className="pricing-card">
              <div className="plan-name">✅ Всем, кто хочет быть готовым к проверке</div>
              <div className="plan-description">Без стресса и лишних хлопот</div>
              <ul className="plan-features">
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Полная готовность к любым проверкам
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Соответствие всем требованиям
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Профессиональная поддержка
                </li>
                <li>
                  <i className="fas fa-check feature-icon"></i>
                  Минимизация рисков
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pricing-note">
            <h4 className="mb-3">Результат</h4>
            <p className="mb-0 text-muted">
              Сервис помогает сократить время на оформление документов с нескольких часов до пары минут — без ошибок и повторных проверок.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <div className="faq-content">
          <div className="faq-header">
            <div className="faq-badge">💬 ЧАСТО ЗАДАВАЕМЫЕ ВОПРОСЫ</div>
            <h2 className="mb-4">Ответы на популярные вопросы</h2>
            <p className="lead text-muted">
              Все, что вам нужно знать о нашем сервисе автоматизации документооборота
            </p>
          </div>
          
          <div className="faq-accordion">
            <div className="faq-item">
              <div 
                className={`faq-question ${activeFaq === 0 ? 'active' : ''}`}
                onClick={() => toggleFaq(0)}
              >
                <span>Можно ли использовать сервис, если у нас нет лицензии?</span>
                <i className={`fas fa-plus faq-icon`}></i>
              </div>
              <div className={`faq-answer ${activeFaq === 0 ? 'active' : ''}`}>
                <p>
                  Да. Мы подбираем шаблоны под формат вашей деятельности — с лицензией или без неё.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div 
                className={`faq-question ${activeFaq === 1 ? 'active' : ''}`}
                onClick={() => toggleFaq(1)}
              >
                <span>Насколько безопасно хранить документы онлайн?</span>
                <i className={`fas fa-plus faq-icon`}></i>
              </div>
              <div className={`faq-answer ${activeFaq === 1 ? 'active' : ''}`}>
                <p>
                  Все данные размещаются на защищённом сервере в России с резервным копированием и шифрованием.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div 
                className={`faq-question ${activeFaq === 2 ? 'active' : ''}`}
                onClick={() => toggleFaq(2)}
              >
                <span>Как подписываются документы?</span>
                <i className={`fas fa-plus faq-icon`}></i>
              </div>
              <div className={`faq-answer ${activeFaq === 2 ? 'active' : ''}`}>
                <p>
                  Подпись ставится онлайн — пальцем или мышью. Это простая электронная подпись, признанная законом.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div 
                className={`faq-question ${activeFaq === 3 ? 'active' : ''}`}
                onClick={() => toggleFaq(3)}
              >
                <span>Можно ли интегрировать сервис с CRM или платформой обучения?</span>
                <i className={`fas fa-plus faq-icon`}></i>
              </div>
              <div className={`faq-answer ${activeFaq === 3 ? 'active' : ''}`}>
                <p>
                  Да, поддерживаем интеграции через API.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div 
                className={`faq-question ${activeFaq === 4 ? 'active' : ''}`}
                onClick={() => toggleFaq(4)}
              >
                <span>Что делать, если нужно изменить шаблон документа?</span>
                <i className={`fas fa-plus faq-icon`}></i>
              </div>
              <div className={`faq-answer ${activeFaq === 4 ? 'active' : ''}`}>
                <p>
                  Мы настраиваем шаблоны индивидуально и при необходимости обновляем под ваши требования.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div 
                className={`faq-question ${activeFaq === 5 ? 'active' : ''}`}
                onClick={() => toggleFaq(5)}
              >
                <span>Сколько стоит подключение?</span>
                <i className={`fas fa-plus faq-icon`}></i>
              </div>
              <div className={`faq-answer ${activeFaq === 5 ? 'active' : ''}`}>
                <p>
                  Вы можете протестировать сервис бесплатно. После демо подберём тариф под количество учеников и документов.
                </p>
              </div>
            </div>
          </div>
          
          <div className="faq-contact">
            <h4 className="mb-3">Хотите убедиться, будет ли сервис работать в вашей организации?</h4>
            <p className="mb-4">
              Протестируйте бесплатно — мы подключим демо-аккаунт, и вы попробуете подписать документ на реальном примере.
            </p>
            <div className="text-center">
              <a href="https://t.me/siliennnce" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                Протестировать сервис бесплатно
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="qwe py-4 border-top bg-light">
        <div className="container d-flex flex-column flex-md-row justify-content-between align-items-center">
          <Logo small />
          <p className="text-muted small mb-0">© 2025 prostopodpishi.ru. Все права защищены.</p>
        </div>
      </footer>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Запросить демо-версию</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Укажите ваше имя"
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Как с вами можно связаться?"
                  required
                />
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleInputChange}
                  className="checkbox-input"
                  id="consent"
                  required
                />
                <label htmlFor="consent" className="checkbox-label">
                Нажимая на кнопку, Вы даете <a href="/personal-agreement" target="_blank">Согласие на обработку своих персональных данных</a> в соответствии с <a href="/privacy"  target="_blank">Политикой конфиденциальности</a>
                </label>
              </div>
              <button type="submit" className="form-submit">
                Отправить заявку
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
